/**
 * The homepage carousels' slot rule ("Nylig lagt ut", "Kjoler"): at most one
 * Zuprize Partner item in any three positions, so a partner that publishes
 * many items at once does not fill the carousel. Private items fill the other
 * places; partner items take the remaining slots only when there are not
 * enough private ones. Within each group the order stays newest first.
 *
 * Search, category pages and "Vis alle" keep plain newest-first order.
 */

// One partner item per this many consecutive positions.
export const PARTNER_SPACING = 3;

/**
 * @param {Array} listings - newest first
 * @param {Function} isPartnerListing - listing => boolean
 * @param {number} size - how many to return
 * @returns {Array} up to `size` listings in carousel order
 */
export const spreadPartnerListings = (listings, isPartnerListing, size) => {
  const privates = listings.filter(l => !isPartnerListing(l));
  const partners = listings.filter(l => isPartnerListing(l));
  const position = new Map(listings.map((l, i) => [l, i]));
  const result = [];
  let lastPartnerAt = -Infinity;

  while (result.length < size && (privates.length > 0 || partners.length > 0)) {
    const at = result.length;
    const partnerAllowed = at - lastPartnerAt >= PARTNER_SPACING;
    const partnerIsNewer =
      partners.length > 0 &&
      (privates.length === 0 || position.get(partners[0]) < position.get(privates[0]));

    if (partners.length > 0 && (privates.length === 0 || (partnerAllowed && partnerIsNewer))) {
      result.push(partners.shift());
      lastPartnerAt = at;
    } else {
      result.push(privates.shift());
    }
  }
  return result;
};

/**
 * Whether the listings in hand are enough to fill `size` places under the
 * rule without falling back to back-to-back partner items: one private item
 * for each place a partner item may not take.
 *
 * @param {Array} listings
 * @param {Function} isPartnerListing
 * @param {number} size
 * @returns {boolean}
 */
export const hasEnoughPrivate = (listings, isPartnerListing, size) => {
  const privateCount = listings.filter(l => !isPartnerListing(l)).length;
  const needed = size - Math.ceil(size / PARTNER_SPACING);
  return privateCount >= Math.min(needed, size);
};
